import React from 'react';
import './CardOverlays.css';

/**
 * Baustein — CardOverlays.
 * Overlay-Vokabular für Kartenbilder — gedacht als `children` von `ui/CardImage`
 * (dessen Overlay-Slot). Glas-Optik über die Overlay-Tokens (`--overlay-*`) aus theme.css.
 *
 *   <CardImage src={…} alt={…}>
 *     <CardOverlays>
 *       <CardOverlays.Actions>
 *         <CardOverlays.Button icon={Minus} label="Menge verringern" onClick={…} />
 *       </CardOverlays.Actions>
 *       <CardOverlays.Corner position="top-left">
 *         <CardOverlays.Badge>×3</CardOverlays.Badge>
 *         <CardOverlays.Badge hover>1,20 €</CardOverlays.Badge>
 *       </CardOverlays.Corner>
 *       <CardOverlays.HighlightRing active={justAdded} />
 *     </CardOverlays>
 *   </CardImage>
 *
 * Root fängt keine Pointer-Events (inset-0, pointer-events:none) — nur Buttons
 * und Badges reaktivieren sie. `Actions` wird erst bei Hover über die Karte
 * sichtbar. `Actions alwaysVisible` zeigt die
 * Gruppe ohne Hover (Touch-Geräte kennen kein Hover) — Klasse
 * `card-overlays__actions--always`. Auf Coarse-Pointer-Geräten (Touch) wächst
 * `.card-overlays__btn` per `@media (pointer: coarse)` auf 36×36px Tippfläche.
 *
 * `CardOverlays.Corner position="top-left" | "top-right"` ist die einzige
 * Ablage für Badges: eine Zeile, in der mehrere Badges nebeneinander stehen
 * (Menge, Preis); ist die Karte zu schmal, brechen sie in weitere Zeilen um und bleiben
 * innerhalb der Kartenbreite. Die Ecke reicht höchstens bis zur Bildmitte (`max-height`,
 * `overflow: hidden`): was dort nicht mehr passt, wird abgeschnitten, statt zur unteren Kante zu
 * wachsen (viele Badges auf einer kleinen Brettkarte). Es gibt nur die beiden oberen Ecken —
 * die untere Bildkante trägt bei Scryfall-Bildern Künstler-Name und ©-Zeile, die eigene Elemente
 * nie verdecken dürfen (Scryfall-Nutzungsregeln in CLAUDE.md). Die Aktions-
 * Gruppe liegt ebenfalls oben rechts; ein `top-right`-Corner und `Actions`
 * schließen sich deshalb in derselben Kachel aus.
 *
 * `CardOverlays.Badge hover` zeigt das Badge erst bei Hover über die Karte —
 * oder über den `ui/CategoryHeader` eines umschließenden Elements mit der
 * Klasse `card-overlays-scope` (Hover über eine Kategorie-Kopfzeile blendet
 * dann die Hover-Badges aller Karten der Kategorie ein). Ohne Hover (Touch)
 * bleibt ein Hover-Badge unsichtbar; wer die Information dort braucht, zeigt
 * sie außerhalb des Bildes.
 *
 * `CardOverlays.Button` ist asChild-tauglich: fremde Props/Handler (z.B. als
 * `trigger` von `ui/ContextMenu`) werden ans `<button>` durchgereicht und mit
 * der eigenen `stopPropagation`-Semantik komponiert statt überschrieben.
 *
 * `CardOverlays.Badge passive` markiert reine Anzeige-Badges (Besitz-Punkt,
 * Menge, Preis): `pointer-events: none`, Klicks fallen zur Kachel darunter
 * durch. Ohne Zeiger-Ereignisse feuert `:hover` nie — ein `title`-Tooltip
 * erscheint auf passiven Badges deshalb nicht, `aria-label` am Inhalt bleibt.
 */
export default function CardOverlays({ children }) {
  return <div className="card-overlays">{children}</div>;
}

CardOverlays.Actions = function CardOverlaysActions({ children, alwaysVisible = false }) {
  const cls = [
    'card-overlays__actions',
    alwaysVisible && 'card-overlays__actions--always',
  ].filter(Boolean).join(' ');
  return <div className={cls}>{children}</div>;
};

CardOverlays.Button = React.forwardRef(function CardOverlaysButton(
  { icon: Icon, label, onClick, onPointerDown, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className="card-overlays__btn"
      data-card-control=""
      aria-label={label}
      title={label}
      // Fremde Handler (z.B. Radix' Menü-Öffnen als asChild-Trigger) laufen zuerst;
      // stopPropagation hält Klick/Drag von der Karte darunter fern.
      onPointerDown={(e) => { onPointerDown?.(e); e.stopPropagation(); }}
      onClick={(e) => { onClick?.(e); e.preventDefault(); e.stopPropagation(); }}
    >
      {Icon && <Icon size={15} aria-hidden />}
    </button>
  );
});
CardOverlays.Button.displayName = 'CardOverlaysButton';

CardOverlays.Corner = function CardOverlaysCorner({ position = 'top-left', children }) {
  return <div className={`card-overlays__corner card-overlays__corner--${position}`}>{children}</div>;
};

CardOverlays.Badge = function CardOverlaysBadge({ passive = false, hover = false, children }) {
  const classes = [
    'card-overlays__badge',
    passive && 'card-overlays__badge--passive',
    hover && 'card-overlays__badge--hover',
  ].filter(Boolean).join(' ');
  // Nicht-passive Badges nehmen den Zeiger an und zählen für `ui/useCardPeek` als Bedienelement.
  return <span className={classes} data-card-control={passive ? undefined : ''}>{children}</span>;
};

/**
 * Voll deckender Accent-Ring auf der Kartenkante — Zustand „Karte gerade
 * hinzugefügt". Pulsiert kurz, blendet dann aus (reine CSS-Animation).
 */
CardOverlays.HighlightRing = function CardOverlaysHighlightRing({ active = false }) {
  if (!active) return null;
  return <div className="card-overlays__ring" aria-hidden />;
};
